/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import MenuItem from "@material-ui/core/MenuItem";
import { Grid } from "@material-ui/core";

//custom libraries
import { Button, RequiredLabel } from "../components";
import { SearchHeader } from "../components/reports/search-header";
import { SearchFilterTitle } from "./../components/reports/search-filter-title";
import TextField from "@material-ui/core/TextField";
import { FormInputLabel } from "./../components/common/FormInputLabel";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useProductsForm } from "../hooks/use-products-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useState } from "react";

const schema = yup.object().shape({
  catalogId: yup.string().required("Campo requerido"),
});

const StyledGridContainer = styled(Grid)`
  margin-bottom: 10px;
  padding: 10px;
`;

export default function ProductReportForm() {
  const { submitForm, listCatalogCategories, categories, catalogs } =
    useProductsForm();

  const [categorySelected, setCategorySelected] = useState("");
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: any) => {
    submitForm(data);
  };

  const handleGetCategories = (catalogId: string) => {
    setCategorySelected("");
    listCatalogCategories({
      responseFormat: "json",
      catalogId,
      contractId: "11501",
      currency: "USD",
      requestType: "GET",
      langId: "",
    });
  };

  return (
    <Grid spacing={2}>
      <Grid item xs={12}>
        <SearchHeader title={t("Report.ReportProduct")} />
      </Grid>
      <Grid item xs={12}>
        <SearchFilterTitle title={t("Report.SearchFilterTitle")} />
      </Grid>

      <StyledGridContainer container xs={12} spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <FormInputLabel text={t("Product.Catalog")} required />
          <TextField
            select
            variant="outlined"
            size="small"
            fullWidth
            error={errors.catalogId ? true : false}
            helperText={errors.catalogId?.message}
            {...register("catalogId")}
            onChange={(evt: any) => {
              if (evt.target.value !== "") {
                handleGetCategories(evt.target.value);
              }
            }}
            SelectProps={{
              displayEmpty: true,
              defaultValue: "",
            }}>
            <MenuItem value="">Seleccionar</MenuItem>
            {catalogs?.map((item: any) => (
              <MenuItem value={item.catalogId} key={item.catalogId}>
                {item.catalogIdentifier}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6} xl={3} md={3}>
          <FormInputLabel text={t("Product.Category")} />
          <TextField
            select
            variant="outlined"
            size="small"
            fullWidth
            error={errors.categoryId ? true : false}
            helperText={errors.categoryId?.message}
            {...register("categoryId")}
            onChange={(evt: any) => {
              setCategorySelected(evt.target.value);
            }}
            value={categorySelected}
            SelectProps={{
              displayEmpty: true,
            }}>
            <MenuItem value="">Seleccionar</MenuItem>
            {categories?.catalogGroupView?.map((item1: any) => {
              if (
                item1.catalogGroupView &&
                Array.isArray(item1.catalogGroupView) &&
                item1.catalogGroupView.length > 1
              ) {
                const initialComponent = [
                  <MenuItem key={item1.uniqueID} value={item1.uniqueID}>
                    {item1.name}
                  </MenuItem>,
                ];
                const components = item1?.catalogGroupView?.map(
                  (item2: any) => {
                    if (
                      item2.catalogGroupView &&
                      Array.isArray(item2.catalogGroupView) &&
                      item2.catalogGroupView.length > 1
                    ) {
                      const initialComponent2 = [
                        <MenuItem key={item2.uniqueID} value={item2.uniqueID}>
                          {item1.name} - {item2.name}
                        </MenuItem>,
                      ];
                      const components2 = item2?.catalogGroupView?.map(
                        (item3: any) => {
                          return (
                            <MenuItem
                              key={item3.uniqueID}
                              value={item3.uniqueID}>
                              {item1.name} - {item2.name} - {item3.name}
                            </MenuItem>
                          );
                        }
                      );
                      return initialComponent2.concat(components2);
                    } else {
                      return (
                        <MenuItem key={item2.uniqueID} value={item2.uniqueID}>
                          {item1.name} - {item2.name}
                        </MenuItem>
                      );
                    }
                  }
                );
                return initialComponent.concat(components);
              } else {
                return (
                  <MenuItem key={item1.uniqueID} value={item1.uniqueID}>
                    {item1.name}
                  </MenuItem>
                );
              }
            })}
          </TextField>
        </Grid>
      </StyledGridContainer>

      <RequiredLabel />

      <Button type="submit" onClick={handleSubmit(onSubmit)} name="Search">
        {t("OrderManagement.Search")}
      </Button>
    </Grid>
  );
}
